import { useState, type CSSProperties } from "react";
import { DEFAULT_SCALE, SAMPLE, measureTileFit, type TileFit } from "./productTileFit";

/**
 * Product photos arrive in every shape (a 4:1 video hub, a 1:3 pendant lamp) and most carry
 * their own white border, so in identical square tiles some products look tiny and others
 * huge. Once a photo loads we measure where the product actually is (non-white, non-clear
 * pixels) and scale and center it so every product covers about the same area of its tile.
 *
 * Measuring needs CORS-readable pixels. Only hosts known to send Access-Control-Allow-Origin
 * load with crossOrigin; anything else (or a failed read) keeps the plain centered fit.
 */
const CORS_HOSTS = /(^|\.)(media-amazon\.com|ssl-images-amazon\.com|walmartimages\.com|scene7\.com|kohlsimg\.com|gamestop\.com)$/i;

const fits = new Map<string, TileFit | null>();

function readFit(img: HTMLImageElement): TileFit | null {
  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h) return null;
  const ratio = SAMPLE / Math.max(w, h);
  const width = Math.max(1, Math.round(w * ratio));
  const height = Math.max(1, Math.round(h * ratio));
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(img, 0, 0, width, height);
    return measureTileFit(context.getImageData(0, 0, width, height).data, width, height);
  } catch {
    // Tainted canvas: the host didn't allow reading the pixels.
    return null;
  }
}

function corsHost(src: string): boolean {
  try {
    return CORS_HOSTS.test(new URL(src).hostname);
  } catch {
    return false;
  }
}

/** A product photo that fills its (square, relatively positioned) tile at a standard size. */
export default function ProductTileImage({ src, className = "", onError }: { src: string; className?: string; onError?: () => void }) {
  const [fit, setFit] = useState<TileFit | null>(() => fits.get(src) ?? null);
  const [cors, setCors] = useState(() => corsHost(src));
  const style: CSSProperties = fit
    ? { transform: `scale(${fit.scale}) translate(${(fit.x * 100).toFixed(2)}%, ${(fit.y * 100).toFixed(2)}%)` }
    : { transform: `scale(${DEFAULT_SCALE})` };
  return (
    <img
      key={cors ? "cors" : "plain"}
      src={src}
      alt=""
      loading="lazy"
      crossOrigin={cors ? "anonymous" : undefined}
      onLoad={(event) => {
        if (!cors || fits.has(src)) return;
        const measured = readFit(event.currentTarget);
        fits.set(src, measured);
        setFit(measured);
      }}
      onError={() => {
        // A host that stopped sending CORS headers: show the photo the plain way.
        if (cors) setCors(false);
        else onError?.();
      }}
      className={`absolute inset-0 h-full w-full object-contain mix-blend-multiply transition-transform duration-150 ${className}`}
      style={style}
    />
  );
}
